import { ReactNode, useCallback, KeyboardEvent } from 'react';
import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';
import Select, { SelectOption } from '@rio-cloud/rio-uikit/Select';

export type SearchModeOption<MODES extends string> = SelectOption & {
    id: MODES;
    placeholder: string;
};

export type MultiModeSearchProps<MODES extends string> = {
    modes: SearchModeOption<MODES>[];
    selectedMode: MODES;
    onSearchModeChange: (newMode: MODES) => void;
    searchTerm: string;
    onSearchTermChange: (newSearchTerm: string) => void;
    onClear: () => void;
    onEnterPressed: () => void;
};

export const MultiModeSearch = <MODE extends string>({
    modes,
    selectedMode,
    onSearchModeChange,
    searchTerm,
    onSearchTermChange,
    onClear,
    onEnterPressed,
}: MultiModeSearchProps<MODE>): ReactNode => {
    const placeholder = modes.find(({ id }) => id === selectedMode)?.placeholder;

    const onSelectChange = useCallback(
        (option: SearchModeOption<MODE> | undefined) => {
            if (option !== undefined) {
                onSearchModeChange(option.id);
            }
        },
        [onSearchModeChange],
    );

    const onKeyPress = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                onEnterPressed();
            }
        },
        [onEnterPressed],
    );

    return (
        // The ClearableInput CSS supports buttons at the end but not at the start.
        // If one tries to add buttons on the start the input is still rounded on
        // that side. Therefore, the code uses the render prop to build a nested
        // input group with a select and an input like shown in the UI KIT:
        // https://uikit.developers.rio.cloud/#/components/select
        <ClearableInput
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={onSearchTermChange}
            onClear={onClear}
            onKeyPress={onKeyPress}
        >
            {(inputProps) => {
                return (
                    <span className="input-group width-500">
                        <span className="input-group-btn">
                            <Select
                                className="width-150"
                                options={modes}
                                value={[selectedMode]}
                                onChange={onSelectChange}
                            />
                        </span>
                        <input {...inputProps} />
                    </span>
                );
            }}
        </ClearableInput>
    );
};
